import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import { ORG_ROLES, strings } from '@constants'
import { migrateOrgToOracle } from '@data/conn/post/oracle'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'
import { DrawerBodyWrapper, DrawerFooterWrapper, TOAST_TYPE } from '@base'
import { Button, Input, MultiSelect, Select, Toggle } from '@baseV2'

import { InputsWrapper } from '../styles'

const PUSH_TYPES = [
	{ label: 'Client', value: ORG_ROLES.marketplaceBuyer.role },
	{ label: 'Vendor', value: ORG_ROLES.marketplaceSupplier.role },
]

// the platforms an org can be pushed as a vendor under
const VENDOR_PLATFORMS = [ORG_ROLES.logisticsSupplier.platform, ORG_ROLES.marketplaceSupplier.platform]

// a client pushed under this commodity goes without a trade name or GSTs
const NO_GST_COMMODITY_ID = 'CO4dR3o-7996'

const PushToOracle = ({ orgId, orgData, update }) => {
	const dispatch = useDispatch()

	// an org can only be pushed under a role it actually holds
	const pushTypes = useMemo(() => {
		const roles = orgData?.roles ?? []
		return PUSH_TYPES.filter((type) => roles.some((role) => role?.role === type.value))
	}, [orgData?.roles])

	// the SUPPLIER platforms the org holds — these are the vendor roles to choose from
	const vendorRoles = useMemo(() => {
		const platforms = new Set(
			(orgData?.roles ?? []).filter((role) => role?.role === ORG_ROLES.marketplaceSupplier.role).map((role) => role?.platform)
		)
		return VENDOR_PLATFORMS.filter((platform) => platforms.has(platform)).map((platform) => ({ id: platform, name: platform }))
	}, [orgData?.roles])

	const [pushType, setPushType] = useState(pushTypes[0]?.value)
	const [vendorRole, setVendorRole] = useState({})
	const [commodity, setCommodity] = useState({})
	const [tradeName, setTradeName] = useState({})
	const [gst, setGst] = useState([])
	const [projectCode, setProjectCode] = useState('')
	const [projectSites, setProjectSites] = useState([])

	const isClient = pushType === ORG_ROLES.marketplaceBuyer.role
	const isVendor = pushType === ORG_ROLES.marketplaceSupplier.role
	const hideGst = isClient && commodity?.commodity_id === NO_GST_COMMODITY_ID

	useEffect(() => {
		setPushType((prev) => (pushTypes.some((type) => type.value === prev) ? prev : pushTypes[0]?.value))
	}, [pushTypes])

	// with a lone vendor role there is nothing to choose, so it is preselected
	useEffect(() => {
		if (isVendor && vendorRoles.length === 1) setVendorRole(vendorRoles[0])
	}, [isVendor, vendorRoles])

	const commodities = orgData?.commodity ?? []

	// The org's GSTs grouped by trade name — one trade name can hold GSTs across several states.
	// Trade names are matched case insensitively, the first spelling seen is the one shown.
	// The same GST number can repeat with different addresses, so it is deduped within its group.
	const gstByTradeName = useMemo(() => {
		const groups = new Map()
		;(orgData?.gst_details ?? []).forEach((item) => {
			if (!item?.gst || !item?.trade_name) return
			const key = item.trade_name.trim().toLowerCase()
			const group = groups.get(key) ?? { name: item.trade_name, items: [] }
			if (group.items.some((existing) => existing.gst === item.gst)) return
			const details = [item?.trade_name, item?.state].filter(Boolean).join(', ')
			group.items.push({ ...item, display_name: `${item.gst}${details ? ` (${details})` : ''}` })
			groups.set(key, group)
		})
		return groups
	}, [orgData?.gst_details])

	const tradeNames = useMemo(() => [...gstByTradeName.entries()].map(([id, group]) => ({ id, name: group.name })), [gstByTradeName])

	// with a lone trade name there is nothing to choose between, so the field is locked
	const singleTradeName = tradeNames.length === 1

	// with no trade name picked, every GST of the org is selectable
	const gstList = useMemo(
		() => (tradeName?.id ? (gstByTradeName.get(tradeName.id)?.items ?? []) : [...gstByTradeName.values()].flatMap((group) => group.items)),
		[gstByTradeName, tradeName?.id]
	)

	// the GST field is read only — a trade name pushes all of its GSTs
	useEffect(() => {
		setGst(tradeName?.id ? gstList : [])
	}, [gstList, tradeName?.id])

	// pushing as a client needs a commodity, as a vendor a role — the GSTs are optional
	const disabled = !pushType || (isClient && !commodity?.commodity_id) || (isVendor && !vendorRole?.id)

	const pushTypeHandler = (value) => {
		setPushType(value)
		setCommodity({})
		setVendorRole({})
		setTradeName({})
		setProjectCode('')
		setProjectSites([])
	}

	const commodityHandler = (value) => {
		setCommodity(value)
		if (value?.commodity_id === NO_GST_COMMODITY_ID) setTradeName({})
		else {
			setProjectCode('')
			setProjectSites([])
		}
	}

	const pushHandler = () => {
		dispatch(showLoader(true))
		// a logistics vendor goes as LOGISTICS, a marketplace one as SUPPLIER
		const role = isVendor && vendorRole?.id === ORG_ROLES.logisticsSupplier.platform ? ORG_ROLES.logisticsSupplier.name : pushType
		// a client sends the GSTs its trade name selected, a vendor the trade name itself — unpicked, they go as null
		migrateOrgToOracle(
			orgId,
			role,
			isClient ? commodity?.commodity_name : null,
			true,
			isClient && !hideGst && gst.length ? gst.map((item) => item?.gst).join(',') : null,
			isVendor ? (tradeName?.name ?? null) : null,
			hideGst ? projectCode.trim() || null : null,
			hideGst && projectSites.length ? projectSites.map((site) => site?.name).join(',') : null
		)
			.then(() => {
				dispatch(showToast(true, strings('msg_oracle_id_push_success'), { type: TOAST_TYPE.SUCCESS }))
				dispatch(toggleDrawer(false))
				update?.()
			})
			.catch((error) =>
				dispatch(showToast(true, error?.response?.data?.message ?? strings('msg_oracle_id_push_fail'), { type: TOAST_TYPE.ERROR }))
			)
			.finally(() => dispatch(showLoader(false)))
	}

	return (
		<>
			<DrawerBodyWrapper>
				<InputsWrapper>
					{pushTypes.length > 0 && <Toggle options={pushTypes} value={pushType} onChange={pushTypeHandler} />}
					{isClient && (
						<>
							<Select
								id='push-to-oracle-commodity'
								label={strings('commodity')}
								placeholder={strings(['select', 'commodity'])}
								data={commodities}
								displayKey='commodity_name'
								primaryKey='commodity_id'
								value={commodity}
								onChange={commodityHandler}
							/>
							{hideGst && (
								<>
									<Input
										id='push-to-oracle-project-code'
										label={strings('project_code')}
										placeholder={strings('project_code')}
										value={projectCode}
										onChange={(e) => setProjectCode(e.target.value)}
									/>
									<MultiSelect
										id='push-to-oracle-project-sites'
										label={strings('project_sites')}
										placeholder={strings('project_sites')}
										data={projectSites}
										displayKey='name'
										primaryKey='name'
										value={projectSites}
										onChange={setProjectSites}
										createNewOption
										hideSelectAll
									/>
								</>
							)}
							{!hideGst && (
								<>
									<Select
										id='push-to-oracle-trade-name'
										label='Trade Name'
										placeholder='Select trade name'
										data={tradeNames}
										displayKey='name'
										primaryKey='id'
										value={tradeName}
										onChange={setTradeName}
										disabled={singleTradeName}
									/>
									<MultiSelect
										id='push-to-oracle-gst'
										label={strings('gst')}
										placeholder={strings(['select', 'gst'])}
										data={gstList}
										displayKey='display_name'
										primaryKey='gst'
										value={gst}
										onChange={setGst}
										disabled
									/>
								</>
							)}
						</>
					)}
					{isVendor && (
						<>
							<Select
								id='push-to-oracle-vendor-role'
								label='Role'
								placeholder='Select role'
								data={vendorRoles}
								displayKey='name'
								primaryKey='id'
								value={vendorRole}
								onChange={setVendorRole}
							/>
							<Select
								id='push-to-oracle-vendor-trade-name'
								label='Trade Name'
								placeholder='Select trade name'
								data={tradeNames}
								displayKey='name'
								primaryKey='id'
								value={tradeName}
								onChange={setTradeName}
								disabled={singleTradeName}
							/>
						</>
					)}
				</InputsWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<Button color='primary' disabled={disabled} onClick={pushHandler}>
					{strings('push_to_oracle')}
				</Button>
			</DrawerFooterWrapper>
		</>
	)
}

export default PushToOracle
